/**
 * FilterHeader Component - Filter Panel Header
 * 
 * Extracted from FilterPanel.tsx to maintain <300 line limit per PROJECT_RULES.md.
 * 
 * Responsibilities:
 * - Display filter title and active filter count badge
 * - Provide "Clear all" functionality when filters are active
 * - Handle collapsible toggle for mobile (44px touch target)
 * - Maintain accessibility with proper ARIA attributes
 */

import React from 'react';

interface FilterHeaderProps {
  activeFilterCount: number;
  hasActiveFilters: boolean;
  isCollapsible: boolean;
  isExpanded: boolean;
  onClearAll: () => void;
  onToggleExpanded: () => void;
}

export const FilterHeader: React.FC<FilterHeaderProps> = React.memo(({
  activeFilterCount,
  hasActiveFilters,
  isCollapsible,
  isExpanded,
  onClearAll,
  onToggleExpanded,
}) => {
  return (
    <div className="flex items-center justify-between p-4 border-b border-gray-200">
      <div className="flex items-center space-x-3">
        <h2 className="text-lg font-medium text-gray-900">Filters</h2>
        {/* Active filter count badge */}
        {hasActiveFilters && (
          <span 
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#E6EFEA] text-[#4A6651]"
            aria-label={`${activeFilterCount} active filters`}
          >
            {activeFilterCount}
          </span>
        )}
      </div>

      <div className="flex items-center space-x-2">
        {/* Clear all filters button */}
        {hasActiveFilters && (
          <button
            onClick={onClearAll}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors px-2 py-0.5 min-h-[44px]"
            aria-label="Clear all filters"
          >
            Clear all
          </button>
        )}

        {/* Mobile collapse toggle - 44px touch target */}
        {isCollapsible && (
          <button
            onClick={onToggleExpanded}
            className="p-3 min-h-[44px] text-gray-400 hover:text-gray-600 transition-colors"
            aria-expanded={isExpanded}
            aria-controls="filter-content"
            aria-label={isExpanded ? 'Collapse filters' : 'Expand filters'}
          >
            <svg
              className={`w-5 h-5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
});

FilterHeader.displayName = 'FilterHeader';

export default FilterHeader;